/**
 * Normalized security events used for operational volume metrics.
 */

import { buildDataDocUrl, fetchRing, getAuthHeaders } from '../core';

export interface SecurityEventItem {
  _id: string;
  threat_event_id?: string;
  candidate_id?: string;
  provider?: string;
  product?: string;
  event_id?: string;
  event_time?: string;
  ingestion_time?: string;
  event_type_raw?: string;
  event_type?: string;
  action?: string;
  actor_id?: string;
  actor_identifier?: string;
  actor_name?: string;
  actor_type?: string;
  authentication_method?: string;
  authentication_mfa?: string | boolean;
  source_ip?: string;
  destination_ip?: string;
  resource_id?: string;
  resource_identifier?: string;
  resource_name?: string;
  resource_type?: string;
  resource_region?: string;
  resource_account?: string;
  target_identifier?: string;
  target_name?: string;
  target_type?: string;
  outcome_reason?: string;
  success?: string | boolean;
  error_code?: string;
  error_message?: string;
  user_agent?: string;
  labels?: unknown;
  raw?: unknown;
  dedup_hash?: string;
  is_duplicate?: boolean | string;
  duplicate_of_event_id?: string;
  dedup_score?: number;
  dedup_related_event_ids?: unknown;
  dedup_related_scores?: unknown;
  [key: string]: unknown;
}

const RING_SECURITY_EVENTS = 'arbitium_security_events';

/** Blueprint stores booleans as strings (`"False"` / `"True"`). */
export function isDuplicateEvent(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  return text === 'true' || text === '1' || text === 'yes';
}

function asSecurityEventDoc(raw: unknown): SecurityEventItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const doc = raw as Record<string, unknown>;
  if (doc.error || doc.success === false) return null;
  if (typeof doc._id === 'string' && doc._id) {
    return doc as SecurityEventItem;
  }
  const nested = doc.item;
  if (nested && typeof nested === 'object') {
    const item = nested as Record<string, unknown>;
    if (typeof item._id === 'string' && item._id && !item.error && item.success !== false) {
      return item as SecurityEventItem;
    }
  }
  return null;
}

export async function getSecurityEventsAll(): Promise<SecurityEventItem[]> {
  const res = await fetchRing<SecurityEventItem>(RING_SECURITY_EVENTS, { all: true });
  return Array.isArray(res.items) ? res.items : [];
}

/** Point lookup when bulk `all=1` omits a linked security event. */
export async function getSecurityEventById(id: string): Promise<SecurityEventItem | null> {
  const docId = String(id || '').trim();
  if (!docId) return null;
  const url = buildDataDocUrl(RING_SECURITY_EVENTS, docId);
  const headers = await getAuthHeaders();
  const res = await fetch(url, { method: 'GET', headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) return null;
  return asSecurityEventDoc(data);
}
